/**
 * #924: the one rule for "a Snowflake destination can load". The five fields a load needs, read
 * by the destination form before Save (`client/src/pages/destination.tsx`), by the binding save
 * (`server/services/BindingService.ts`, 400 `VALIDATION_FAILED` naming each missing field) and by
 * the runner's per-tenant gate (`runner/steps/pushLoad.ts` `pushReadiness`,
 * `TENANT_SNOWFLAKE_LOAD_NOT_READY (binding_incomplete)`), so the form and the gate cannot drift.
 *
 * Only presence is checked here, never a value's shape, and a private key is only "set or not":
 * no caller hands this module a key.
 */

/** The fields a Snowflake load needs, in the order the form shows them. */
export const SNOWFLAKE_LOAD_FIELDS = [
  "snowflakeAccount",
  "snowflakeUser",
  "snowflakePrivateKey",
  "snowflakeDatabase",
  "snowflakeSchema",
] as const;
export type SnowflakeLoadField = (typeof SNOWFLAKE_LOAD_FIELDS)[number];

/** The plain-text load fields; the private key is a secret reference. */
export type SnowflakeLoadTextField = Exclude<SnowflakeLoadField, "snowflakePrivateKey">;

/** Each field's name in a sentence ("the destination has no database"). */
export const SNOWFLAKE_LOAD_FIELD_NAMES: Record<SnowflakeLoadField, string> = {
  snowflakeAccount: "account",
  snowflakeUser: "user",
  snowflakePrivateKey: "private key",
  snowflakeDatabase: "database",
  snowflakeSchema: "schema",
};

/** What the rule reads: each text field as stored or typed, and whether a private key is set. */
export type SnowflakeLoadState = Record<SnowflakeLoadTextField, string | null | undefined> & {
  snowflakePrivateKey: boolean;
};

/** The load fields `state` lacks, in `SNOWFLAKE_LOAD_FIELDS` order; empty when it can load. */
export function missingSnowflakeLoadFields(state: SnowflakeLoadState): SnowflakeLoadField[] {
  return SNOWFLAKE_LOAD_FIELDS.filter((field) =>
    field === "snowflakePrivateKey" ? !state.snowflakePrivateKey : !state[field]?.trim(),
  );
}

/** The field error a save shows on one missing field (no em dash: `scripts/check-copy.mjs`). */
export function snowflakeLoadFieldIssue(field: SnowflakeLoadField): string {
  return field === "snowflakePrivateKey"
    ? "Set the private key. A Snowflake load signs in with it."
    : `Enter the ${SNOWFLAKE_LOAD_FIELD_NAMES[field]}. A Snowflake load needs it.`;
}

/** "the Snowflake destination has no database" / "... has no account, user or database". */
export function missingSnowflakeLoadSentence(fields: readonly SnowflakeLoadField[]): string {
  const names = fields.map((field) => SNOWFLAKE_LOAD_FIELD_NAMES[field]);
  if (names.length === 0) return "the Snowflake destination has every field a load needs";
  const list =
    names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} or ${names.at(-1)}`;
  return `the Snowflake destination has no ${list}`;
}

/**
 * Whether a binding save body changes what a Snowflake load reads: the destination type, one of
 * the five fields, or the private key. Only such a save is held to the rule, so a save of the dbt
 * repository, the Teams webhook or the warehouse name alone is never refused for a field it did
 * not touch. Takes the body's shape loosely: the client's and the server's bodies both fit.
 */
export function touchesSnowflakeLoad(body: {
  destinationType?: unknown;
  snowflakeAccount?: unknown;
  snowflakeUser?: unknown;
  snowflakeDatabase?: unknown;
  snowflakeSchema?: unknown;
  secrets?: { snowflakePrivateKey?: unknown } | null;
}): boolean {
  return (
    body.destinationType !== undefined ||
    body.snowflakeAccount !== undefined ||
    body.snowflakeUser !== undefined ||
    body.snowflakeDatabase !== undefined ||
    body.snowflakeSchema !== undefined ||
    body.secrets?.snowflakePrivateKey !== undefined
  );
}
